import React, { useState, useEffect } from "react";
import { SidePanel, Tag, Text, Label, Divider, Link, ButtonGroup, Button, Spinner } from '@nike/eds';
import { Exception } from '../../graphql/graphql';
import { User } from '../../graphql/graphql';
import './exception-detail.css';
import { formatDate } from '../../shared/Utils';
import { mapStatusToColor } from '../../shared/Utils';
import { getUrgencyTag } from '../../shared/Utils';
import { AuditComments } from './AuditComments';
import { AssigneeChooser } from './AssigneeChooser';
import { ShortcutLink } from '../../shared/ShortcutLink';
import { useApolloClient, useQuery, ApolloError } from "@apollo/client";
import { GET_EXCEPTION_FIELDS, GET_EXCEPTION } from '../../graphql/exceptions.graphql';
import { GqlErrorModal } from '../../shared/GqlErrorModal';
import { MouseSpinner } from "../../shared/MouseSpinner";

interface ExceptionDetailProps {
  isOpen: boolean;
  onClose: () => void;
  exceptionId: string | null | undefined;
  currentUser?: User;
  assignToMeHandler?: () => void;
  assignToUserHandler?: (user: User) => void;
  markAsOpenHandler?: () => void;
  markInProgressHandler?: () => void;
  retryHandler?: () => void;
  assignLoading?: boolean;
  retryLoading?: boolean;
  inProgressLoading?: boolean;
  openLoading?: boolean;
  assignError?: ApolloError | undefined; 
  retryError?: ApolloError | undefined;
  inProgressError?: ApolloError | undefined;
  openError?: ApolloError | undefined;
}

interface ExceptionDetailContentProps {
  exception: Exception;
  currentUser?: User;
  assignToMeHandler?: () => void;
  assignToUserHandler?: (user: User) => void;
  markAsOpenHandler?: () => void;
  markInProgressHandler?: () => void;
  retryHandler?: () => void;
  assignLoading?: boolean;
  retryLoading?: boolean;
  inProgressLoading?: boolean;
  openLoading?: boolean;
}

interface ExceptionHeaderProps {
  exception: Exception;
}

export const ExceptionHeader = ({ exception }: ExceptionHeaderProps) => {
  const urgencyTag = getUrgencyTag(exception);
  const plannedDischargeDate = exception.targetResolutionDate ? formatDate(exception.targetResolutionDate) : "N/A";
  
  return (
    <div className="exception-header-wrapper">
      {/* Critical Information Section */}
      <div className="critical-info-section">
        <div className="eds-grid eds-grid--m-cols-2 eds-gap--32">
          <div>
            <Label font={'body-3'}>Exception ID</Label>
            <div className="exceptionIdWrapper">
              <Text title={exception.alias} font={'body-1'} className='ellipsis'>{exception.alias}</Text>
              <ShortcutLink className="eds-spacing--ml-8 xsmall" size="small" url={`/exceptions/${encodeURIComponent(exception.id)}`} />
            </div>
          </div>
          
          <div>
            <Label font={'body-3'}>Urgency</Label>
            <Tag color={urgencyTag.color} size="medium" style={{ whiteSpace: "nowrap" }}>
              {urgencyTag.text}
            </Tag>
          </div>
        </div>
      </div>

      <Divider className="eds-spacing--mt-8 eds-spacing--mb-8" />

      {/* Shipment Information Section */}
      <div className="shipment-info-section">
        <div className="eds-grid eds-grid--m-cols-2 eds-gap--32 eds-spacing--mb-12">
          <div>
            <Label font={'body-3'}>Vehicle Details</Label>
            <Link font={'body-1'} target='_blank' href={`/transport-vehicles/${exception.consignment?.equipmentVehicle?.id}`}>
              {exception.consignment?.equipmentVehicle?.voyageNumber}
            </Link>
          </div>
          
          <div>
            <Label font={'body-3'}>ETA</Label>
            <Text as='span' font={'body-1'}>{plannedDischargeDate}</Text>
          </div>
        </div>

        <div>
          <Label font={'body-3'}>Consignment ID</Label>
          <Link font={'body-1'}  title={exception.consignment.id} target='_blank' href={`/consignment/${exception.consignment.id}?tab=exceptions&step=${encodeURIComponent(exception.stepTitle)}`}>
            {exception.consignment.id}
          </Link>
        </div>
      </div>

      <Divider className="eds-spacing--mt-8 eds-spacing--mb-8" />
    </div>
  );
};

const ExceptionDetailContent = ({ 
  exception,
  currentUser,
  assignToMeHandler, 
  assignToUserHandler,
  markAsOpenHandler,
  markInProgressHandler,
  retryHandler,
  assignLoading,
  retryLoading,
  inProgressLoading,
  openLoading,
}: ExceptionDetailContentProps) => {

  const statusText = exception.status.replace(/_/g, " ");
  const statusColor = mapStatusToColor(exception.status);


return (
  <div className="exception-detail-content eds-grid eds-gap--8 eds-spacing--mb-16">
    <div className="eds-grid eds-grid--m-cols-2  eds-gap--32">
      <div className="">
        <Label font={'body-3'} className="eds-spacing--mb-8">Exception Type</Label>
        <Text font={'body-1'}>{exception.description}</Text>
      </div>
      <div>
        <Label font={'body-3'}>Status</Label>
          <Tag
            color={statusColor}
            size="medium"
            style={{whiteSpace: "nowrap"}}
          >{statusText}</Tag>
      </div>
    </div>

    <Divider className="eds-spacing--mt-8 eds-spacing--mb-8" />
    
    <AssigneeChooser 
      exception={exception}
      currentUser={currentUser}
      assignToMeHandler={assignToMeHandler} 
      assignToUserHandler={assignToUserHandler} 
      customsTerritory={exception.customsTerritory}
      assignLoading={assignLoading}
    />

    <Divider className="eds-spacing--mb-8" />   
    
    <div className="eds-grid--col-full">
      <Label font={'body-3'}>Guidance</Label>
      <Text font={'body-1'}>{exception.guidance || "N/A"}</Text>
    </div>

    <Divider className="eds-spacing--mb-8" />   

    <Label font={'body-3'}>Actions</Label>
    
    <ButtonGroup className="eds-spacing--mb-8" style={{'position': 'relative'}}>
      <Button
        variant="primary"
        size="small"
        disabled={exception.status === "RESOLVED" || exception.status === "RETRYING" || exception.retryable === false}
        onClick={retryHandler}
        title={exception.retryable === false ? "This exception is not retryable." : exception.status === "RETRYING" ? "This exception is currently being retried." : exception.status === "RESOLVED" ? "This exception is resolved." : "Retry this exception."}
      >
        Retry
      </Button>
      
      { exception.status === "IN_PROGRESS" && (
      <Button
        variant="primary"
        size="small"
        onClick={markAsOpenHandler}
      >
        Mark as Open
      </Button>
      )}
      
      { exception.status !== "RESOLVED" && exception.status !== "IN_PROGRESS" && exception.status !== "RETRYING" && (
      <Button
        variant="primary"
        size="small"
        onClick={markInProgressHandler}
      >
        Mark in Progress
      </Button>
      )}

      {(retryLoading || inProgressLoading || openLoading) && <div style={{position: 'absolute', width: '100%', height: '100%', top: 0, left: 0, background: 'rgba(255, 255, 255, 0.8)'}} className="eds-flex eds-flex--align-items-center eds-flex--justify-content-center"><Spinner className="" size="large" /></div>}
    </ButtonGroup>

    <Divider className="eds-spacing--mb-8" />

    <AuditComments exception={exception} />


  </div>
)};

export const ExceptionDetail = ({
  isOpen, 
  onClose, 
  exceptionId,
  currentUser,
  assignToMeHandler, 
  assignToUserHandler, 
  markAsOpenHandler,
  markInProgressHandler,
  retryHandler,
  assignLoading,
  retryLoading,
  inProgressLoading,
  openLoading,
  assignError,
  retryError,
  inProgressError,
  openError
}: ExceptionDetailProps) => {
  const client = useApolloClient();
  const [localError, setLocalError] = useState<ApolloError | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // Try to get the exception from the cache first
  let latestException: Exception | null = null;
  if (exceptionId) {
    latestException = client.readFragment({
      id: client.cache.identify({ __typename: "Exception", id: exceptionId }),
      fragment: GET_EXCEPTION_FIELDS,
    });
  }

  // If not in cache, fetch from server
  const { data, loading, error } = useQuery(GET_EXCEPTION, {
    variables: { id: exceptionId },
    skip: !!latestException || !exceptionId,
    fetchPolicy: "cache-first",
  });

  // Combine error handling for query and mutation errors
  useEffect(() => {
    const firstError =
      assignError ||
      retryError ||
      inProgressError ||
      openError ||
      error;

    if (firstError) {
      setLocalError(firstError);
      setShowErrorModal(true);
    }
  }, [assignError, retryError, inProgressError, openError, error]);

  
  if (!latestException && data?.exception?.get) {
    latestException = data.exception.get;
  }

  // Expose detail open flag BEFORE any early returns so hook order is stable
  useEffect(() => {
    if (isOpen && latestException) {
      (window as any).__EXCEPTIONS_DETAIL_OPEN__ = true;
    }
  }, [isOpen, latestException]);

  if (!exceptionId) return null;
  if (!latestException && loading) return <MouseSpinner />;

  const exceptionKey = latestException ? latestException.id + latestException.consignment.billOfLadingNbr : '';

  return (
    <>
      {showErrorModal && localError && (
        <GqlErrorModal
          error={localError}
          onClose={() => setShowErrorModal(false)}
        />
      )}
      {latestException && (
      <SidePanel
        className='exception-detail-side-panel'
        displayCheckOverride={true}
        hideScrim={true}
        onDismiss={onClose}
        isOpen={isOpen}
        headerSlot={<div style={{ height: '16px'}}></div>}
        hideFade={true}
      >
        <>
        <ExceptionHeader exception={latestException} />
        <ExceptionDetailContent
          key={exceptionKey}
          exception={latestException}
          currentUser={currentUser}
          assignToMeHandler={assignToMeHandler}
          assignToUserHandler={assignToUserHandler}
          markAsOpenHandler={markAsOpenHandler}
          markInProgressHandler={markInProgressHandler}
          retryHandler={retryHandler}
          assignLoading={assignLoading}
          retryLoading={retryLoading}
          inProgressLoading={inProgressLoading}
          openLoading={openLoading}
        />
        </>
      </SidePanel>
      )}
    </>
  );
}